import { cloneDeep } from "lodash";
import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import { SimulationChangeType, SimulationMimshakData } from "../../../interfaces/DTO/simulation";
import { FundTransferDetails, Mimshak, PolicyStatus } from "../../../interfaces/DTO/mislaka_xml_models";
import { formatCurrency, formatHandlerCode, formatPercentage } from "../../../utils/namesFormater";
import { formatHandlerCodeUniversal } from "../../../utils/handlerCodeUniversalFormatter";
import { ManualProductType } from "../../../interfaces/DTO/manualMimshakData";
import { formatProductTypeUniversal } from "../../../utils/productTypeUniversalFormatter";
import {
  PathFundSelection,
  applyFundsSumToMimshak,
  mergeDuplicateInvestmentPathsByPathCode,
  replacePathsInProduct,
} from "../../../hooks/feeLab/useNewProductHandlers";
import { displayPolicyNumber } from "../../../utils/manualProductTypeFormatter";

/**
 * Quick actions on a client's policies, collected in a basket ("מה עוד ללקוח זה?") and saved as one simulation
 * with its tasks. Each action becomes a change on a SimulationMimshakData item - the same shape the lab produces,
 * so the workflow and the daily-report tracking are the same whichever way the change was made.
 */

export interface TransferAction {
  kind: "transfer";
  id: string;
  /** one source, or several of the same product type (consolidating funds into one - full transfers only) */
  sources: MimshakData[];
  /** part of the money of a single source - the source stays open */
  isPartial: boolean;
  /** the money transferred - the sources' balance, or the part of a partial transfer */
  amount: number;
  /** the receiving policy - an existing one of the client, or a new (virtual "transfered-") one */
  target: MimshakData;
  isNewTarget: boolean;
  /** the fees at the receiving fund after the transfer (null - unchanged / unknown) */
  newSavingFee: number | null;
  newDepositFee: number | null;
}

export interface WithdrawalAction {
  kind: "withdrawal";
  id: string;
  policy: MimshakData;
  amount: number;
}

export interface PathChangeAction {
  kind: "path_change";
  id: string;
  policy: MimshakData;
  /** the new investment paths at the policy's handler, with the amount in each */
  selections: PathFundSelection[];
}

export interface OneTimeDepositAction {
  kind: "one_time_deposit";
  id: string;
  policy: MimshakData;
  amount: number;
}

export interface RecurringDepositAction {
  kind: "recurring_deposit";
  id: string;
  policy: MimshakData;
  newAmount: number;
  /** a new standing order: the day of the month it is charged - none: the producer's default */
  dayOfMonth?: number | null;
}

/** a power of attorney to view the policy's / portfolio's data at its producer (a Mislaka producer or an investment house) */
export interface ViewPowerOfAttorneyAction {
  kind: "view_power_of_attorney";
  id: string;
  policy: MimshakData;
}

export type QuickAction =
  | TransferAction
  | OneTimeDepositAction
  | RecurringDepositAction
  | WithdrawalAction
  | PathChangeAction
  | ViewPowerOfAttorneyAction;

export const newActionId = () => Math.random().toString(36).substring(2, 9);

export const policyKey = (p: MimshakData) => `${p.contact_id}|${p.handler_code}|${p.policy_number}`;

// virtual policy numbers, as the lab's (the daily-report linking knows both - DailyReportPolicyUtil.VIRTUAL_POLICY_PREFIXES)
const NEW_TARGET_PREFIX = "transfered-"; // a new fund a transfer goes into
const NEW_FUND_PREFIX = "generated-"; // a new fund opened for a deposit / standing order

/** a new fund opened in this basket (not a real policy yet - MimshakData.new_policy is also set on real ones) */
export const isBasketNewFund = (p: MimshakData) =>
  !!p.policy_number?.startsWith(NEW_TARGET_PREFIX) || !!p.policy_number?.startsWith(NEW_FUND_PREFIX);

/** a new fund opened for a deposit / standing order (OPEN_NEW) */
const isOpenedFund = (p: MimshakData) => !!p.policy_number?.startsWith(NEW_FUND_PREFIX);

/** a new empty fund of a product type at a handler - as the lab's new product (no balance, no path chosen yet) */
export const buildNewFund = (contactId: number, reportedPeriod: string, handlerCode: string, productType: number): MimshakData => {
  const policyNumber = NEW_FUND_PREFIX + newActionId();
  const today = new Date().toISOString().split("T")[0];
  const data: Mimshak = {
    products: [
      {
        details: { handler_code: handlerCode, product_type: productType, producer_code: "", adjusted_product_type: productType },
        policies_or_accounts: [
          {
            policy_number: policyNumber,
            status: PolicyStatus.ACTIVE,
            investment_paths: [],
            program_name: "",
            update_date: reportedPeriod,
            accumulated_savings: [],
            liquidity_summaries: [{ liquidity_date: today, liquidity_amount: 0 }],
          },
        ],
      },
    ],
  } as Mimshak;
  return {
    id: Date.now(),
    policy_number: policyNumber,
    handler_code: handlerCode,
    product_type: productType,
    adjusted_product_type: productType,
    contact_id: contactId,
    reported_period: reportedPeriod,
    data,
    new_policy: true,
    funds_sum: 0,
    saving_fee: null,
    deposit_fee: 0,
    deposit_amount: 0,
    withdrawal_amount: 0,
    agent_code: null,
    report_origin: 2,
    report_type: 1,
  } as unknown as MimshakData;
};

export const productLabel = (p: MimshakData) => formatProductTypeUniversal(p.adjusted_product_type ?? p.product_type);

export const policyLabel = (p: MimshakData) => `${productLabel(p)} · ${formatHandlerCodeUniversal(p.handler_code)}`;

/** an investment portfolio loaded by hand (ManualMimshakData) - only a view power of attorney applies to it */
export const isInvestmentPortfolio = (p: MimshakData) => p.product_type === ManualProductType.INVESTMENT_PORTFOLIO;

/** an investment path - its name and its fund id */
export const fundLabel = (fund: { fund_name?: string | null; fund_id: number }) => `${fund.fund_name ?? ""} (${fund.fund_id})`;

const actionPolicy = (action: QuickAction): MimshakData => (action.kind === "transfer" ? action.target : action.policy);

/** the client the action is for (a source of a transfer, the policy of a deposit) */
export const actionContactId = (action: QuickAction): number =>
  (action.kind === "transfer" ? action.sources[0] : action.policy).contact_id;

export const sumFunds = (policies: MimshakData[]) => policies.reduce((sum, p) => sum + (p.funds_sum || 0), 0);

/** how much leaves each source: all of it, or the part of a partial transfer (a single source) */
const transferredFromSources = (action: TransferAction): { source: MimshakData; amount: number }[] =>
  action.isPartial
    ? [{ source: action.sources[0], amount: action.amount }]
    : action.sources.map((source) => ({ source, amount: source.funds_sum || 0 }));

export const describeAction = (action: QuickAction): string => {
  switch (action.kind) {
    case "transfer": {
      const from =
        action.sources.length === 1
          ? policyLabel(action.sources[0])
          : `איחוד ${action.sources.length} קופות ${productLabel(action.sources[0])}`;
      const fee = action.newSavingFee != null ? ` · ד"נ צבירה ${formatPercentage(action.newSavingFee)}` : "";
      return `${action.isPartial ? "ניוד חלקי" : "ניוד"}: ${from} ← ${formatHandlerCode(action.target.handler_code)}${
        action.isNewTarget ? " (קופה חדשה)" : displayPolicyNumber(action.target.policy_number) ? ` (${action.target.policy_number})` : ""
      } · ${formatCurrency(action.amount)}${fee}`;
    }
    case "withdrawal":
      return `משיכה: ${policyLabel(action.policy)} · ${formatCurrency(action.amount)}`;
    case "path_change":
      return `${isBasketNewFund(action.policy) ? "מסלול לקופה החדשה" : "שינוי מסלול"}: ${policyLabel(action.policy)} ← ${action.selections
        .map((s) => fundLabel(s.fund))
        .join(", ")}`;
    case "one_time_deposit":
      return `הפקדה חד פעמית: ${policyLabel(action.policy)}${isOpenedFund(action.policy) ? " (קופה חדשה)" : ""} · ${formatCurrency(action.amount)}`;
    case "view_power_of_attorney":
      return `ייפוי כוח לצפייה: ${policyLabel(action.policy)}${
        displayPolicyNumber(action.policy.policy_number) ? ` (${action.policy.policy_number})` : ""
      }`;
    case "recurring_deposit":
      return action.policy.recurrent_deposit
        ? `שינוי הוראת קבע: ${policyLabel(action.policy)} · ${formatCurrency(action.policy.recurrent_deposit)} ← ${formatCurrency(action.newAmount)}`
        : `הוראת קבע חדשה: ${policyLabel(action.policy)}${isOpenedFund(action.policy) ? " (קופה חדשה)" : ""} · ${formatCurrency(action.newAmount)}${
            action.dayOfMonth ? ` · ביום ${action.dayOfMonth} בחודש` : ""
          }`;
  }
};

/**
 * The client's policies as they will be after the basket's actions: a fully transferred source is gone,
 * the balances move with partial transfers / withdrawals / transfers into an existing fund, and a new transfer
 * target is added (so a deposit can be made into it).
 */
export const policiesAfterActions = (policies: MimshakData[], actions: QuickAction[]): MimshakData[] => {
  const transfers = actions.filter((a): a is TransferAction => a.kind === "transfer");
  const closed = new Set(transfers.filter((a) => !a.isPartial).flatMap((a) => a.sources.map(policyKey)));
  const balanceChange = new Map<string, number>();
  const addChange = (key: string, amount: number) => balanceChange.set(key, (balanceChange.get(key) ?? 0) + amount);

  transfers.forEach((a) => {
    if (a.isPartial) addChange(policyKey(a.sources[0]), -a.amount);
    if (!a.isNewTarget) addChange(policyKey(a.target), a.amount);
  });
  actions.forEach((a) => a.kind === "withdrawal" && addChange(policyKey(a.policy), -a.amount));

  // new funds opened in the basket - by a transfer into a new fund, or for a deposit / standing order
  const newFunds = new Map<string, MimshakData>();
  transfers.filter((a) => a.isNewTarget).forEach((a) => newFunds.set(policyKey(a.target), a.target));
  actions.forEach(
    (a) => (a.kind === "one_time_deposit" || a.kind === "recurring_deposit") && isOpenedFund(a.policy) && newFunds.set(policyKey(a.policy), a.policy)
  );

  return [
    ...policies
      .filter((p) => !closed.has(policyKey(p)))
      .map((p) =>
        balanceChange.has(policyKey(p)) ? { ...p, funds_sum: Math.max(0, (p.funds_sum || 0) + balanceChange.get(policyKey(p))!) } : p
      ),
    ...Array.from(newFunds.values()).map((p) =>
      balanceChange.has(policyKey(p)) ? { ...p, funds_sum: Math.max(0, (p.funds_sum || 0) + balanceChange.get(policyKey(p))!) } : p
    ),
  ];
};

/** the new funds of the basket that a remaining action still opens (a transfer into it, a deposit / standing order on it) */
const openedFundKeys = (actions: QuickAction[]): Set<string> =>
  new Set(
    actions.flatMap((a) =>
      a.kind === "transfer" && a.isNewTarget
        ? [policyKey(a.target)]
        : (a.kind === "one_time_deposit" || a.kind === "recurring_deposit") && isOpenedFund(a.policy)
        ? [policyKey(a.policy)]
        : []
    )
  );

/**
 * Removes an action and the actions that depend on it - an action on a new fund that no remaining action opens
 * (e.g. a path for the new fund of a removed transfer).
 */
export const removeActionWithDependents = (actions: QuickAction[], id: string): QuickAction[] => {
  let remaining = actions.filter((a) => a.id !== id);
  for (;;) {
    const opened = openedFundKeys(remaining);
    const next = remaining.filter((a) => {
      const policy = actionPolicy(a);
      return !isBasketNewFund(policy) || opened.has(policyKey(policy)) || (a.kind === "transfer" && a.isNewTarget);
    });
    if (next.length === remaining.length) return next;
    remaining = next;
  }
};

const transferDetails = (source: MimshakData, amount: number): FundTransferDetails => {
  const today = new Date().toISOString().split("T")[0];
  return {
    transferring_corporation: source.handler_code,
    transferring_fund_id: (source.adjusted_product_type ?? source.product_type) as unknown as number,
    transferring_fund_name: source.policy_number,
    payment_period_code: String(source.id),
    deposit_date: today,
    deposit_type_code: 0,
    contribution_type: 0,
    depositor_type: 0,
    deposit_amount_paid: amount,
    salary_base: 0,
    salary_month: source.reported_period,
    payment_period: 0,
    fixing_date: today,
  } as FundTransferDetails;
};

/**
 * A new receiving policy at another (or the same) handler - as the lab's transfer into a new product:
 * a virtual "transfered-" policy number (the daily-report linking matches it by handler + product type),
 * the transferred amount, and each source recorded in funds_transfer. Built from the largest source;
 * the investment paths of another handler do not apply - no path is chosen yet.
 */
export const buildNewTransferTarget = (
  sources: MimshakData[],
  handlerCode: string,
  partialAmount: number | null = null
): MimshakData => {
  const policyNumber = NEW_TARGET_PREFIX + newActionId();
  const template = sources.reduce((largest, s) => ((s.funds_sum || 0) > (largest.funds_sum || 0) ? s : largest));
  const transferred = partialAmount != null ? [{ source: sources[0], amount: partialAmount }] : sources.map((s) => ({ source: s, amount: s.funds_sum || 0 }));
  const total = transferred.reduce((sum, t) => sum + t.amount, 0);
  const handlerChanged = sources.some((s) => s.handler_code !== handlerCode);
  const target = applyFundsSumToMimshak(cloneDeep(template), total);
  const product = target.data?.products?.[0];
  if (product) {
    product.details = { ...product.details, handler_code: handlerCode };
    const policy = product.policies_or_accounts?.[0];
    if (policy) {
      policy.policy_number = policyNumber;
      policy.funds_transfer = transferred.map((t) => transferDetails(t.source, t.amount));
      if (handlerChanged) policy.investment_paths = [];
    }
  }
  return {
    ...target,
    id: Date.now(),
    policy_number: policyNumber,
    handler_code: handlerCode,
    new_policy: true,
    saving_fee: null,
    funds_sum: total,
    deposit_amount: 0,
    recurrent_deposit: undefined,
    withdrawal_amount: 0,
    transfer_in: total,
    fund_ids: handlerChanged ? [] : target.fund_ids,
  };
};

/** An existing receiving policy - adds the transfers to its funds_transfer */
const withTransferIn = (target: MimshakData, transferred: { source: MimshakData; amount: number }[]): MimshakData => {
  const total = transferred.reduce((sum, t) => sum + t.amount, 0);
  const updated = cloneDeep(target);
  const policy = updated.data?.products?.[0]?.policies_or_accounts?.[0];
  if (policy) policy.funds_transfer = [...(policy.funds_transfer ?? []), ...transferred.map((t) => transferDetails(t.source, t.amount))];
  return {
    ...updated,
    funds_sum: (target.funds_sum || 0) + total,
    transfer_in: (target.transfer_in || 0) + total,
  };
};

/**
 * The simulation items: one item per receiving policy, with a change type per action on it
 * (each change type becomes its own task with its own workflow). The transferring funds are not items -
 * they are referenced from the receiving fund's funds_transfer.
 */
/**
 * A new standing order's charge day - in the policy's deposit collection details (a monthly direct debit), where
 * the Mislaka reports it; the producer's form is filled from there, and the next reports can be compared with it.
 */
const withStandingOrderDay = <T extends MimshakData>(item: T, day: number | null | undefined): T => {
  if (!day || !item.data?.products) return item;
  const data = cloneDeep(item.data);
  for (const product of data.products ?? []) {
    for (const policy of product.policies_or_accounts ?? []) {
      if (policy.policy_number !== item.policy_number) continue;
      const others = (policy.deposit_collection_details ?? []).filter(
        (d) => !(d.payment_method === DIRECT_DEBIT && d.payment_frequency === MONTHLY)
      );
      policy.deposit_collection_details = [
        ...others,
        { payer_name: "", payer_id_number: "", payment_method: DIRECT_DEBIT, payment_frequency: MONTHLY, payment_day_in_month: day },
      ];
    }
  }
  return { ...item, data };
};
// mislaka_xml_models PaymentMethod.DIRECT_DEBIT / PaymentFrequency.MONTHLY
const DIRECT_DEBIT = 2;
const MONTHLY = 4;

export const buildSimulationItems = (actions: QuickAction[]): SimulationMimshakData[] => {
  const items = new Map<string, SimulationMimshakData & { notes: string[] }>();

  for (const action of actions) {
    const key = policyKey(actionPolicy(action));
    const policy = actionPolicy(action);
    // a fund opened for a deposit / standing order - joining it is a change (a task) of its own, as the lab's new product
    const base = items.get(key) ?? {
      ...policy,
      id: 0,
      change_types: isOpenedFund(policy) ? [SimulationChangeType.OPEN_NEW] : [],
      notes: [],
    };
    let item: SimulationMimshakData & { notes: string[] } = { ...base, notes: [...base.notes, describeAction(action)] };

    // a change type once per item - each one becomes one task (two transfers into the same fund are one join)
    const withChange = (changeTypes: SimulationChangeType[] | undefined, changeType: SimulationChangeType) =>
      changeTypes?.includes(changeType) ? changeTypes : [...(changeTypes ?? []), changeType];
    const hadDeposit = !!item.change_types?.includes(SimulationChangeType.DEPOSIT);
    const hadWithdrawal = !!item.change_types?.includes(SimulationChangeType.WITHDRAWAL);

    switch (action.kind) {
      case "transfer":
        item = {
          ...item,
          ...(action.isNewTarget ? {} : withTransferIn(item, transferredFromSources(action))),
          saving_fee: action.newSavingFee ?? item.saving_fee,
          deposit_fee: action.newDepositFee ?? item.deposit_fee,
          // on the receiving fund: CLOSE_AND_MOVE - the source is closed, PARTIAL_TRANSFER - it stays open
          // (as the daily report reports the join: JOIN_POLICY + CLOSE_AND_MOVE / PARTIAL_TRANSFER)
          change_types: withChange(
            item.change_types,
            action.isPartial ? SimulationChangeType.PARTIAL_TRANSFER : SimulationChangeType.CLOSE_AND_MOVE
          ),
        };
        break;
      case "withdrawal":
        item = {
          ...item,
          withdrawal_amount: (hadWithdrawal ? item.withdrawal_amount || 0 : 0) + action.amount,
          change_types: withChange(item.change_types, SimulationChangeType.WITHDRAWAL),
        };
        break;
      case "path_change": {
        const withPaths = mergeDuplicateInvestmentPathsByPathCode(cloneDeep(item));
        replacePathsInProduct(withPaths, item.policy_number, action.selections);
        item = {
          ...item,
          data: withPaths.data,
          fund_ids: action.selections.map((s) => s.fund.fund_id),
          // the path of a new fund is part of its join (as in the lab) - not a separate path change
          change_types: isBasketNewFund(action.policy) ? item.change_types : withChange(item.change_types, SimulationChangeType.CHANGE_FUND),
        };
        break;
      }
      case "one_time_deposit":
        item = {
          ...item,
          deposit_amount: (hadDeposit ? item.deposit_amount || 0 : 0) + action.amount,
          change_types: withChange(item.change_types, SimulationChangeType.DEPOSIT),
        };
        break;
      case "recurring_deposit":
        item = {
          // a new standing order's day - in the policy's collection details (not for a change of an existing one)
          ...(action.policy.recurrent_deposit ? item : withStandingOrderDay(item, action.dayOfMonth)),
          recurrent_deposit: action.newAmount,
          change_types: withChange(
            item.change_types,
            action.policy.recurrent_deposit ? SimulationChangeType.CHANGE_RECURRENT_DEPOSIT : SimulationChangeType.NEW_RECURRENT_DEPOSIT
          ),
        };
        break;
      case "view_power_of_attorney":
        item = { ...item, change_types: withChange(item.change_types, SimulationChangeType.VIEW_POWER_OF_ATTORNEY) };
        break;
    }
    items.set(key, item);
  }

  return Array.from(items.values()).map(({ notes, ...item }) => ({ ...item, note: notes.join(" | ") }));
};
